import { Box, Button, Paper, Typography } from "@mui/material";
import grey from "@mui/material/colors/grey";
import { PMPopUp } from "@pimo/pimo-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { ChangeEvent, useRef, useState } from "react";
import { Project } from "vmd-types";

import {
  DcRelevanceImportResult,
  importDcRelevance,
} from "../../helpers/excel-importer";
import { DcRelevancePreviewModal } from "./dc-relevance-preview-modal";
import { OeMultiSelect } from "./oe-multi-select";

type DcRelevanceImportSectionProps = {
  language: string;
  projects: Project[];
};

const buttonSx = {
  textTransform: "none",
  borderRadius: "4px",
  padding: "7px 16px",
  whiteSpace: "nowrap",
  minWidth: "fit-content",
} as const;

export const DcRelevanceImportSection = ({
  language,
  projects,
}: DcRelevanceImportSectionProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<DcRelevanceImportResult | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [appliedCount, setAppliedCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const t = (key: string, params?: Record<string, string | number>) =>
    getLocalizedMessage(
      language,
      `manageProjects.dcRelevanceImport.${key}`,
      params
    );

  // pimo-l10n has no plural rules.
  const plural = (key: string, count: number) =>
    t(`${key}${count === 1 ? "One" : "Other"}`, { count });

  const resetResult = () => {
    setPreview(null);
    setAppliedCount(null);
    setError(null);
  };

  const handleFileSelected = (e: ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] ?? null);
    e.target.value = "";
    resetResult();
  };

  const run = async (dryRun: boolean) => {
    if (!file || selectedProjectIds.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const result = await importDcRelevance(file, selectedProjectIds, dryRun);
      if (dryRun) {
        setPreview(result);
        setPreviewOpen(true);
      } else {
        setPreview(null);
        setFile(null);
        setAppliedCount(result.changes.length);
        document.dispatchEvent(new CustomEvent("distribution-import:refresh"));
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const toYes = preview?.changes.filter((c) => c.to === "Yes").length ?? 0;
  const toNo = preview?.changes.filter((c) => c.to === "No").length ?? 0;

  return (
    <Paper
      sx={{
        borderRadius: 3,
        boxShadow: "none",
        border: "1px solid",
        borderColor: grey[300],
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        flex: 0,
      }}
    >
      <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>
        {t("title")}
      </Typography>
      <Typography sx={{ fontSize: "14px", color: grey[700] }}>
        {t("description")}
      </Typography>

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: "none" }}
        onChange={handleFileSelected}
      />

      <Box sx={{ maxWidth: 480 }}>
        <OeMultiSelect
          label={t("oes")}
          placeholder={t("searchOes")}
          projects={projects}
          value={selectedProjectIds}
          onChange={(projectIds) => {
            setSelectedProjectIds(projectIds);
            resetResult();
          }}
        />
      </Box>

      <Box
        sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}
      >
        <Button
          variant="outlined"
          onClick={() => fileInputRef.current?.click()}
          disabled={loading}
          sx={buttonSx}
        >
          {t("upload")}
        </Button>
        <Button
          variant="outlined"
          onClick={() => run(true)}
          disabled={loading || !file || selectedProjectIds.length === 0}
          sx={buttonSx}
        >
          {t("preview")}
        </Button>
        <Button
          variant="contained"
          onClick={() => setConfirmOpen(true)}
          disabled={loading || !preview || preview.changes.length === 0}
          sx={{ ...buttonSx, color: "#fff" }}
        >
          {t("apply")}
        </Button>
      </Box>

      {file && (
        <Typography sx={{ fontSize: "14px", color: grey[700] }}>
          {file.name}
        </Typography>
      )}

      {preview && (
        <Typography sx={{ fontSize: "14px", color: grey[700] }}>
          {t("previewSummary", {
            changes: preview.changes.length,
            yes: toYes,
            no: toNo,
            unchanged: preview.unchanged,
            notFound: preview.excelIdsNotFound.length,
          })}
        </Typography>
      )}

      {appliedCount !== null && (
        <Typography sx={{ fontSize: "14px", color: grey[700] }}>
          {t("success", {
            count: appliedCount,
          })}
        </Typography>
      )}

      {error && (
        <Typography
          sx={{ fontSize: "14px", color: "error.main", fontWeight: 500 }}
        >
          {error}
        </Typography>
      )}

      {preview && confirmOpen && (
        <PMPopUp
          open
          title={t("confirmTitle")}
          description={t("confirmMessage", {
            apps: plural("appCount", preview.changes.length),
            oes: plural("oeCount", selectedProjectIds.length),
          })}
          onClose={() => setConfirmOpen(false)}
          maxWidth="sm"
          cancelButtonText={t("cancel")}
          cancelButtonFireEvent={() => setConfirmOpen(false)}
          cancelButtonColor="white"
          confirmationButtonText={t("apply")}
          confirmationButtonFireEvent={() => {
            setConfirmOpen(false);
            run(false);
          }}
        />
      )}

      {preview && previewOpen && (
        <DcRelevancePreviewModal
          open
          onClose={() => setPreviewOpen(false)}
          preview={preview}
          language={language}
        />
      )}
    </Paper>
  );
};
